/**
 * This device's line to the outlet hub (08-clients.md "Outlet hub"). The hub is optional:
 * a till with no hub (or none reachable) works from its own queue and local store alone;
 * with one, it also relays what it does so the other devices on the LAN see it.
 *
 * Configuration (set by the shell when the terminal is registered with the outlet's hub;
 * in a browser by hand, for development):
 *   localStorage `qrkey.hub.v1` = { "token": "<terminal token>", "baseUrl": "https://…" }
 * `baseUrl` may be left out when the shell's bridge can `discoverHub()` (mDNS). The token
 * is the terminal's HMAC token for the hub (outlet-hub auth.ts), never the hub secret.
 */
import { HubClient } from '@qrkey/outlet-hub/client';
import type { DeviceBridge } from '@qrkey/device-bridge';
import { terminalStore } from '../app/terminal.js';

const KEY = 'qrkey.hub.v1';

export interface HubLinkConfig {
  token: string;
  baseUrl?: string;
}

export function getHubLinkConfig(): HubLinkConfig | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as Partial<HubLinkConfig>;
    return typeof c.token === 'string' && c.token.length > 0
      ? { token: c.token, ...(typeof c.baseUrl === 'string' ? { baseUrl: c.baseUrl } : {}) }
      : null;
  } catch {
    return null;
  }
}

export function setHubLinkConfig(config: HubLinkConfig | null): void {
  try {
    if (config) window.localStorage.setItem(KEY, JSON.stringify(config));
    else window.localStorage.removeItem(KEY);
  } catch {
    // Not persisted; nothing else depends on it.
  }
}

export interface HubLinkDeps {
  bridge?: DeviceBridge | undefined;
  fetch?: typeof fetch | undefined;
}

/** A client for the hub this device should use now, or null (no hub configured or found). */
export async function resolveHubClient(deps: HubLinkDeps = {}): Promise<HubClient | null> {
  const config = getHubLinkConfig();
  const terminal = terminalStore.get();
  if (!config || !terminal) return null;
  let baseUrl = config.baseUrl;
  if (!baseUrl) {
    const found = await deps.bridge?.discoverHub?.().catch(() => null);
    if (!found) return null;
    baseUrl = `https://${found.host}:${found.port}`;
  }
  return new HubClient({
    baseUrl,
    terminalId: terminal.terminalId,
    token: config.token,
    ...(deps.fetch ? { fetch: deps.fetch } : {}),
  });
}
